import { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const W = 420;
const H = 620;
const GRAVITY      = 0.28;
const ARROW_SPEED  = 18;
const MAX_PULL     = 90;
const BOW_X        = W / 2;
const BOW_Y        = H - 95;
const TOTAL_ARROWS = 3;
const TARGET_R     = 28;

const LEVELS = [
  { id:1, targets:2, swingAmp:22, swingSpeed:0.025, ropes:[160,220],  pts:100, label:"Kevum Hunt",     si:"කෙවුම් දඩයම" },
  { id:2, targets:3, swingAmp:30, swingSpeed:0.032, ropes:[130,200,280], pts:150, label:"Kokis Chaos",  si:"කොකිස් කෝලාහලය" },
  { id:3, targets:3, swingAmp:38, swingSpeed:0.042, ropes:[120,195,285], pts:200, label:"Festival Fury",si:"උත්සව රෝෂය" },
  { id:4, targets:4, swingAmp:44, swingSpeed:0.05,  ropes:[100,175,255,330], pts:250, label:"Master Archer",si:"ශූර弓ශූරයා" },
];

const TARGET_TYPES = [
  { type:"kevum",  emoji:"🍮", color:"#D4A017", glow:"#FFD700", label:"Kevum",  pts:100 },
  { type:"kokis",  emoji:"🌸", color:"#FF69B4", glow:"#FF1493", label:"Kokis",  pts:150 },
  { type:"mango",  emoji:"🥭", color:"#FF8C00", glow:"#FFA500", label:"Mango",  pts:80  },
  { type:"lotus",  emoji:"🪷", color:"#FF1493", glow:"#FF69B4", label:"Lotus",  pts:200 },
  { type:"golden", emoji:"⭐", color:"#FFD700", glow:"#FFFF00", label:"Golden", pts:300 },
];

const rand  = (a, b) => Math.random() * (b - a) + a;
const pick  = arr => arr[Math.floor(Math.random() * arr.length)];
let   _uid  = 0;
const uid   = () => ++_uid;

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Noto+Sans+Sinhala:wght@700;900&family=Exo+2:wght@400;700;900&display=swap');

*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; -webkit-tap-highlight-color:transparent; user-select:none; touch-action:none; }
body { background:#07020e; overflow:hidden; width:100vw; height:100dvh; }

:root {
  --gold:#FFD700; --orange:#FF6B35; --red:#FF1744;
  --green:#69F0AE; --cyan:#00E5FF; --purple:#AA00FF;
}

/* ── KEYFRAMES ── */
@keyframes fadeUp      { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
@keyframes stampIn     { 0%{transform:scale(2.4) rotate(-8deg);opacity:0} 65%{transform:scale(.92) rotate(2deg)} 100%{transform:scale(1) rotate(0);opacity:1} }
@keyframes goldGlow    { 0%,100%{filter:drop-shadow(0 0 6px var(--gold))} 50%{filter:drop-shadow(0 0 22px var(--gold)) drop-shadow(0 0 40px var(--orange))} }
@keyframes pulse       { 0%,100%{opacity:.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.18)} }
@keyframes lanternSway { 0%,100%{transform:rotate(-8deg)} 50%{transform:rotate(8deg)} }
@keyframes confettiFall{ 0%{transform:translateY(-10px) rotate(0);opacity:1} 100%{transform:translateY(200px) rotate(600deg);opacity:0} }
@keyframes bgPulse     { 0%,100%{opacity:.35} 50%{opacity:.65} }
@keyframes bowPull     { 0%{transform:scale(1)} 100%{transform:scale(1.06)} }
@keyframes arrowFly    { 0%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
@keyframes targetHit   { 0%{transform:scale(1)} 30%{transform:scale(1.5)} 100%{transform:scale(0);opacity:0} }
@keyframes floatScore  { 0%{transform:translateY(0) scale(1);opacity:1} 100%{transform:translateY(-70px) scale(1.3);opacity:0} }
@keyframes shimmer     { 0%,100%{background-position:200% center} 50%{background-position:0% center} }
@keyframes ropeSwing   {}
@keyframes starTwinkle { 0%,100%{opacity:.2;transform:scale(1)} 50%{opacity:1;transform:scale(1.5)} }
@keyframes trailFade   { from{opacity:.7} to{opacity:0} }
@keyframes unlockBounce{ 0%{transform:scale(0) rotate(-15deg);opacity:0} 60%{transform:scale(1.12) rotate(3deg)} 100%{transform:scale(1) rotate(0);opacity:1} }
@keyframes bowStringPull { 0%{d:path('M0,-55 Q0,0 0,55')} 100%{d:path('M0,-55 Q-30,0 0,55')} }
@keyframes torchFlicker{ 0%,100%{transform:scaleY(1) skewX(0deg)} 25%{transform:scaleY(1.1) skewX(3deg)} 75%{transform:scaleY(.9) skewX(-2deg)} }

.root {
  width:100vw; height:100dvh;
  display:flex; flex-direction:column; align-items:center;
  background:#07020e; overflow:hidden; position:relative;
  font-family:'Exo 2', sans-serif;
}

/* Ambient glow */
.root::before {
  content:''; position:absolute; inset:0; pointer-events:none; z-index:0;
  background:
    radial-gradient(ellipse at 50% 0%,    rgba(255,107,53,.1) 0%, transparent 55%),
    radial-gradient(ellipse at 20% 80%,   rgba(255,215,0,.07) 0%, transparent 45%),
    radial-gradient(ellipse at 80% 60%,   rgba(170,0,255,.06) 0%, transparent 40%);
  animation: bgPulse 5s ease-in-out infinite;
}

/* ── HUD ── */
.hud {
  width:100%; max-width:480px;
  padding:.5rem .8rem .3rem;
  background:linear-gradient(to bottom,rgba(0,0,0,.88) 55%,transparent);
  display:flex; align-items:center; gap:.4rem;
  z-index:20; flex-shrink:0;
}
.score-box { background:rgba(255,215,0,.1); border:1px solid rgba(255,215,0,.25); border-radius:10px; padding:.22rem .65rem; }
.score-lbl { font-size:.42rem; color:rgba(255,215,0,.5); letter-spacing:2px; }
.score-val { font-size:1.5rem; font-weight:900; color:var(--gold); line-height:1; font-family:'Cinzel Decorative',serif; }
.hud-mid   { flex:1; display:flex; flex-direction:column; align-items:center; gap:.15rem; }
.level-name{ font-size:.6rem; color:rgba(255,255,255,.45); letter-spacing:2px; }
.arrow-row { display:flex; gap:.3rem; }
.arrow-icon{ font-size:1.1rem; transition:all .3s; }
.arrow-icon.used { opacity:.18; filter:grayscale(1); }
.level-badge { background:rgba(255,107,53,.15); border:1px solid rgba(255,107,53,.3); border-radius:10px; padding:.22rem .6rem; text-align:center; }
.level-lbl { font-size:.42rem; color:rgba(255,107,53,.55); letter-spacing:2px; }
.level-val { font-size:.9rem; font-weight:700; color:var(--orange); line-height:1; }

/* ── GAME CANVAS ── */
.canvas {
  flex:1; width:100%; max-width:480px;
  position:relative; overflow:hidden;
}
.canvas svg { width:100%; height:100%; display:block; position:absolute; top:0; left:0; }

/* Float score */
.float-score {
  position:absolute; pointer-events:none; z-index:40;
  font-family:'Cinzel Decorative',serif; font-size:1.4rem; font-weight:700;
  text-shadow:0 2px 10px rgba(0,0,0,.8);
  animation:floatScore .9s ease-out forwards;
}

/* ── MENU ── */
.menu {
  width:100%; height:100dvh;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:.9rem; padding:1.5rem 1.2rem;
  position:relative; z-index:5;
  background:radial-gradient(ellipse at 50% 20%,#2d0a0a 0%,#07020e 100%);
  overflow:hidden;
}
.menu-title {
  font-family:'Cinzel Decorative',serif;
  font-size:clamp(1.8rem,8vw,3rem); color:var(--gold);
  text-align:center; line-height:1.1;
  animation:goldGlow 2.5s ease-in-out infinite;
  text-shadow:0 4px 0 rgba(0,0,0,.5);
}
.menu-si {
  font-family:'Noto Sans Sinhala',sans-serif; font-weight:900;
  font-size:clamp(1rem,4.5vw,1.5rem); color:rgba(255,210,80,.82); text-align:center;
}
.menu-preview { font-size:clamp(2.5rem,10vw,4rem); animation:pulse 2s ease-in-out infinite; filter:drop-shadow(0 0 16px var(--gold)); }
.menu-desc {
  background:rgba(255,255,255,.05); border:1px solid rgba(255,215,0,.15);
  border-radius:16px; padding:.75rem 1.1rem; max-width:305px;
  color:rgba(255,240,180,.75); font-size:.78rem; text-align:center; line-height:1.7;
}
.level-grid { display:grid; grid-template-columns:1fr 1fr; gap:.4rem; width:100%; max-width:320px; }
.level-card {
  background:rgba(255,255,255,.06); border:1.5px solid rgba(255,255,255,.1);
  border-radius:12px; padding:.55rem .7rem; cursor:pointer; transition:all .2s;
  display:flex; align-items:center; gap:.45rem;
}
.level-card.sel { background:rgba(255,215,0,.15); border-color:rgba(255,215,0,.45); }
.level-card:hover:not(.sel) { border-color:rgba(255,255,255,.25); }
.lc-num { width:26px; height:26px; border-radius:50%; background:linear-gradient(135deg,var(--gold),var(--orange)); color:#1a0010; font-weight:900; font-size:.8rem; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-family:'Exo 2',sans-serif; }
.lc-name{ font-size:.72rem; font-weight:700; color:#fff; font-family:'Noto Sans Sinhala',sans-serif; line-height:1.2; }
.lc-en  { font-size:.58rem; color:rgba(255,255,255,.45); letter-spacing:.5px; }
.play-btn {
  background:linear-gradient(135deg,var(--gold),var(--orange));
  color:#1a0010; border:none; border-radius:50px; padding:1rem 3rem;
  font-size:1.1rem; font-family:'Cinzel Decorative',serif; cursor:pointer;
  letter-spacing:2px; box-shadow:0 0 28px rgba(255,215,0,.45);
  transition:transform .12s; animation:goldGlow 2s ease-in-out infinite;
}
.play-btn:active { transform:scale(.95); }
.hs-row { font-size:.7rem; color:rgba(255,255,255,.28); letter-spacing:2px; }

/* ── RESULT / GAME OVER ── */
.overlay {
  position:absolute; inset:0; z-index:60;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:.6rem; background:rgba(0,0,0,.6); backdrop-filter:blur(4px);
  pointer-events:none;
}
.result-stamp {
  font-family:'Cinzel Decorative',serif;
  font-size:clamp(2rem,9vw,3.2rem); text-align:center; line-height:1;
  text-shadow:0 4px 22px rgba(0,0,0,.7);
  animation:stampIn .5s cubic-bezier(.34,1.56,.64,1) both; letter-spacing:2px;
}
.result-si { font-family:'Noto Sans Sinhala',sans-serif; font-weight:700; font-size:1rem; color:rgba(255,255,255,.8); animation:fadeUp .4s .2s both; }
.result-pts{ font-family:'Cinzel Decorative',serif; font-size:1.8rem; letter-spacing:2px; animation:fadeUp .4s .35s both; }
.confetti-p{ position:absolute; pointer-events:none; z-index:65; border-radius:2px; animation:confettiFall ease-out forwards; }

/* ── GAMEOVER SCREEN ── */
.gameover {
  width:100%; height:100dvh; display:flex; flex-direction:column;
  align-items:center; justify-content:center; gap:.9rem; padding:1.5rem;
  background:linear-gradient(160deg,#0d0005 0%,#07020e 100%); z-index:5; position:relative;
}
.go-title { font-family:'Cinzel Decorative',serif; font-size:clamp(1.6rem,7vw,2.4rem); color:var(--gold); text-align:center; animation:goldGlow 2s infinite; }
.go-card  { background:rgba(255,255,255,.05); border:1px solid rgba(255,215,0,.2); border-radius:22px; padding:1.4rem 1.8rem; width:100%; max-width:295px; text-align:center; animation:fadeUp .5s both; }
.go-score { font-family:'Cinzel Decorative',serif; font-size:3.8rem; background:linear-gradient(135deg,var(--gold),var(--orange)); -webkit-background-clip:text; -webkit-text-fill-color:transparent; line-height:1; }
.go-grid  { display:grid; grid-template-columns:1fr 1fr; gap:.4rem; margin-top:.85rem; }
.go-stat  { background:rgba(255,255,255,.04); border-radius:9px; padding:.4rem; }
.go-sl    { font-size:.43rem; color:rgba(255,255,255,.3); letter-spacing:2px; }
.go-sv    { font-size:.95rem; color:#fff; font-weight:800; }
.new-rec  { color:var(--purple); font-size:.78rem; letter-spacing:2px; display:block; margin-top:.4rem; animation:pulse 1s infinite; font-family:'Cinzel Decorative',serif; }
.go-btns  { display:flex; gap:.6rem; width:100%; max-width:295px; }
.go-a     { flex:1; background:linear-gradient(135deg,var(--gold),var(--orange)); color:#1a0010; border:none; padding:.85rem; border-radius:40px; font-size:.88rem; font-family:'Cinzel Decorative',serif; cursor:pointer; letter-spacing:1px; transition:transform .12s; }
.go-b     { flex:1; background:rgba(255,255,255,.08); color:rgba(255,255,255,.8); border:1px solid rgba(255,255,255,.18); padding:.85rem; border-radius:40px; font-size:.88rem; cursor:pointer; transition:transform .12s; }
.go-a:active,.go-b:active { transform:scale(.95); }

/* Aim guide dot */
.aim-dot { position:absolute; pointer-events:none; border-radius:50%; background:rgba(255,215,0,.7); transform:translate(-50%,-50%); z-index:35; }

/* Pull indicator */
.pull-ring {
  position:absolute; border-radius:50%; pointer-events:none; z-index:32;
  border:2px solid rgba(255,107,53,.6); transform:translate(-50%,-50%);
  transition:all .05s;
}

/* Lantern */
.lantern { position:absolute; animation:lanternSway ease-in-out infinite; transform-origin:top center; font-size:1.6rem; z-index:3; filter:drop-shadow(0 0 8px var(--orange)); pointer-events:none; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// CONFETTI
// ─────────────────────────────────────────────────────────────────────────────
function Confetti() {
  return <>{Array.from({length:28},(_,i)=>(
    <div key={i} className="confetti-p" style={{
      left:`${5+Math.random()*90}%`, top:"8%",
      width:5+Math.random()*9, height:5+Math.random()*9,
      background:["#FFD700","#FF6B35","#FF1493","#4CAF50","#00E5FF","#D500F9","#FF5722"][i%7],
      animationDuration:`${.6+Math.random()*.9}s`,
      animationDelay:`${Math.random()*.4}s`,
      borderRadius:Math.random()>.5?"50%":"2px",
    }}/>
  ))}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
// SCENE SVG — background elements
// ─────────────────────────────────────────────────────────────────────────────
function SceneSVG() {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={{zIndex:1}}>
      <defs>
        <linearGradient id="skyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#0d0520"/>
          <stop offset="45%"  stopColor="#1a0830"/>
          <stop offset="100%" stopColor="#2d1205"/>
        </linearGradient>
        <linearGradient id="groundGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"  stopColor="#1a0d04"/>
          <stop offset="100%" stopColor="#0a0802"/>
        </linearGradient>
        <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#FFF9E7" stopOpacity=".95"/>
          <stop offset="60%" stopColor="#FFE082" stopOpacity=".4"/>
          <stop offset="100%" stopColor="transparent"/>
        </radialGradient>
        <filter id="torchBlur">
          <feGaussianBlur stdDeviation="4"/>
        </filter>
        <filter id="glowFilt">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Sky */}
      <rect width={W} height={H} fill="url(#skyGrad)"/>

      {/* Stars */}
      {Array.from({length:55},(_,i)=>(
        <circle key={i}
          cx={rand(8,W-8)} cy={rand(5,H*0.52)}
          r={rand(.6,2.2)}
          fill="#fff" opacity={rand(.2,.9)}
          style={{animation:`starTwinkle ${1.5+Math.random()*3.5}s ${Math.random()*4}s ease-in-out infinite`}}
        />
      ))}

      {/* Moon */}
      <circle cx={W*0.82} cy={H*0.08} r={32} fill="url(#moonGlow)"/>
      <circle cx={W*0.82} cy={H*0.08} r={52} fill="rgba(255,230,120,0.07)" style={{filter:"blur(10px)"}}/>

      {/* Distant temple silhouette */}
      <g opacity="0.35">
        <rect x={30} y={H*0.42} width={12} height={H*0.18} fill="#1a0a2e"/>
        <polygon points={`30,${H*.42} 36,${H*.36} 42,${H*.42}`} fill="#1a0a2e"/>
        <rect x={50} y={H*0.44} width={10} height={H*0.16} fill="#1a0a2e"/>
        <polygon points={`50,${H*.44} 55,${H*.39} 60,${H*.44}`} fill="#1a0a2e"/>
        <rect x={W-55} y={H*0.41} width={14} height={H*0.19} fill="#1a0a2e"/>
        <polygon points={`${W-55},${H*.41} ${W-48},${H*.34} ${W-41},${H*.41}`} fill="#1a0a2e"/>
      </g>

      {/* Trees silhouette */}
      {[0.04,0.12,0.85,0.92].map((xf,i)=>(
        <g key={i} transform={`translate(${W*xf},${H*0.56})`} opacity="0.5">
          <rect x={-4} y={-H*.2} width={8} height={H*.2} fill="#0d0820"/>
          <circle cx={0} cy={-H*.2} r={H*.09} fill="#0d0820"/>
          <circle cx={-10} cy={-H*.15} r={H*.065} fill="#0d0820"/>
          <circle cx={10} cy={-H*.17} r={H*.06} fill="#0d0820"/>
        </g>
      ))}

      {/* Decorative arch beam (top) */}
      <rect x={0} y={0} width={W} height={24} fill="linear-gradient(#3d1a00,#2d1000)" opacity=".0"/>
      {/* Beam */}
      <rect x={12} y={8} width={W-24} height={20} rx={5} fill="#3d1a00" stroke="#8B4513" strokeWidth={1.5}/>
      <rect x={12} y={8} width={W-24} height={7} rx={4} fill="rgba(255,215,0,0.15)"/>

      {/* Beam poles */}
      <rect x={12} y={8} width={14} height={H*.28} rx={4} fill="#5D2E0C" stroke="#3d1a00" strokeWidth={1}/>
      <rect x={W-26} y={8} width={14} height={H*.28} rx={4} fill="#5D2E0C" stroke="#3d1a00" strokeWidth={1}/>

      {/* Decorative flags */}
      {Array.from({length:9},(_,i)=>(
        <polygon key={i}
          points={`${24+i*46},8 ${54+i*46},8 ${39+i*46},20`}
          fill={["#FF5722","#FFD700","#4CAF50","#2196F3","#9C27B0","#FF9800","#E91E63","#00BCD4","#FF5722"][i]}
          opacity=".88"
        />
      ))}

      {/* Torch lights on poles */}
      {[26,W-22].map((tx,i)=>(
        <g key={i}>
          {/* Torch glow */}
          <circle cx={tx} cy={H*.3} r={28} fill="rgba(255,120,20,.15)" style={{filter:"url(#torchBlur)"}}/>
          <circle cx={tx} cy={H*.3} r={16} fill="rgba(255,180,30,.25)" style={{filter:"url(#torchBlur)"}}/>
          {/* Torch flames */}
          <ellipse cx={tx} cy={H*.28} rx={8} ry={12} fill="#FF6D00" opacity=".9" style={{animation:"torchFlicker 1.2s ease-in-out infinite"}}/>
          <ellipse cx={tx} cy={H*.27} rx={5} ry={8} fill="#FFCC02" opacity=".85" style={{animation:"torchFlicker 1s .3s ease-in-out infinite"}}/>
          <ellipse cx={tx} cy={H*.26} rx={3} ry={5} fill="#FFF9C4" opacity=".9" style={{animation:"torchFlicker .8s .6s ease-in-out infinite"}}/>
          {/* Torch stick */}
          <rect x={tx-3} y={H*.3} width={6} height={H*.05} rx={2} fill="#5D4037"/>
        </g>
      ))}

      {/* Ground */}
      <rect x={0} y={H*0.6} width={W} height={H*0.4} fill="url(#groundGrad)"/>
      <line x1={0} y1={H*0.6} x2={W} y2={H*0.6} stroke="rgba(255,107,53,0.3)" strokeWidth={1.5}/>
      {/* Ground details */}
      {[H*.63, H*.68, H*.74].map((y,i)=>(
        <line key={i} x1={0} y1={y} x2={W} y2={y} stroke="rgba(0,0,0,.2)" strokeWidth={1}/>
      ))}

      {/* Lantern strings */}
      {[0.15,0.35,0.55,0.72,0.88].map((xf,i)=>(
        <g key={i}>
          <line x1={W*xf} y1={28} x2={W*xf} y2={H*.22} stroke="rgba(255,200,50,.25)" strokeWidth={1}/>
          <ellipse cx={W*xf} cy={H*.23} rx={9} ry={12}
            fill={["#FF5722","#FFD700","#E91E63","#FF9800","#9C27B0"][i]}
            opacity=".85" style={{filter:"url(#glowFilt)"}}/>
          <ellipse cx={W*xf} cy={H*.23} rx={9} ry={12}
            fill={["#FF5722","#FFD700","#E91E63","#FF9800","#9C27B0"][i]}
            opacity=".3" style={{filter:"blur(6px)"}}/>
        </g>
      ))}

      {/* Ground archer platform */}
      <ellipse cx={W/2} cy={H-80} rx={55} ry={14} fill="rgba(93,46,12,.6)" stroke="rgba(255,107,53,.25)" strokeWidth={1}/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BOW SVG (centered, rotatable)
// ─────────────────────────────────────────────────────────────────────────────
function BowSVG({ angle, pullDist, pulling }) {
  const pull = Math.min(pullDist / MAX_PULL, 1);
  const bowBend = pull * 18;
  const strX = -pull * 22;

  return (
    <g transform={`translate(${BOW_X},${BOW_Y}) rotate(${angle})`}>
      {/* Glow */}
      {pulling && (
        <circle r={20+pull*25} fill="rgba(255,215,0,.08)" style={{filter:"blur(6px)"}}/>
      )}

      {/* Bow wood */}
      <path
        d={`M 0,-58 Q ${bowBend+16},0 0,58`}
        fill="none" stroke="#8B4513" strokeWidth={9} strokeLinecap="round"
      />
      <path
        d={`M 0,-58 Q ${bowBend+10},0 0,58`}
        fill="none" stroke="#D2691E" strokeWidth={5} strokeLinecap="round"
      />
      {/* Wood grain */}
      <path
        d={`M 0,-44 Q ${bowBend+8},0 0,44`}
        fill="none" stroke="rgba(255,200,100,.2)" strokeWidth={1.5} strokeLinecap="round"
      />

      {/* Bow string */}
      <path
        d={`M 0,-54 Q ${strX},0 0,54`}
        fill="none" stroke="#FFF8E7" strokeWidth={pulling ? 2.5 : 1.5}
        strokeLinecap="round"
        style={{filter: pulling ? "drop-shadow(0 0 3px #FFD700)" : "none"}}
      />

      {/* Arrow on string */}
      {pulling && (
        <g>
          {/* Arrow shaft */}
          <line x1={strX} y1={0} x2={18} y2={0} stroke="#8B6914" strokeWidth={3} strokeLinecap="round"/>
          {/* Arrowhead */}
          <polygon points={`18,0 14,-4 14,4`} fill="#E0E0E0"/>
          <polygon points={`18,0 14,-4 14,4`} fill="url(#arrowTip)" stroke="#9E9E9E" strokeWidth={.5}/>
          {/* Fletching */}
          <polygon points={`${strX},0 ${strX+8},-5 ${strX+6},0`} fill="#FF5722" opacity=".9"/>
          <polygon points={`${strX},0 ${strX+8},5 ${strX+6},0`} fill="#FF9800" opacity=".9"/>
          <line x1={strX+3} y1={-5} x2={strX+3} y2={5} stroke="#4E342E" strokeWidth={1}/>
        </g>
      )}

      {/* Archer grip tape */}
      <rect x={-4} y={-8} width={8} height={16} rx={3} fill="#4E342E"/>
      {[-4,0,4].map(y=>(
        <line key={y} x1={-4} y1={y} x2={4} y2={y} stroke="#3E2723" strokeWidth={1.5}/>
      ))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FLYING ARROW
// ─────────────────────────────────────────────────────────────────────────────
function ArrowSVG({ arrow }) {
  const angle = Math.atan2(arrow.vy, arrow.vx) * 180 / Math.PI;
  return (
    <g transform={`translate(${arrow.x},${arrow.y}) rotate(${angle})`} opacity={1}>
      {/* Trail */}
      {arrow.trail?.map((t,i)=>(
        <circle key={i} cx={t.x-arrow.x} cy={t.y-arrow.y} r={2-i*.15}
          fill="rgba(255,215,0,.4)" opacity={.5-i*.04}/>
      ))}
      {/* Glow */}
      <circle r={8} fill="rgba(255,200,50,.15)" style={{filter:"blur(4px)"}}/>
      {/* Shaft */}
      <line x1={-18} y1={0} x2={8} y2={0} stroke="#A0622A" strokeWidth={3.5} strokeLinecap="round"/>
      <line x1={-18} y1={0} x2={8} y2={0} stroke="#D4A04A" strokeWidth={1.5} strokeLinecap="round"/>
      {/* Head */}
      <polygon points="8,0 3,-4 3,4" fill="#BDBDBD"/>
      <polygon points="8,0 3,-4 3,4" fill="#E0E0E0" opacity=".6"/>
      <line x1={3} y1={0} x2={8} y2={0} stroke="#9E9E9E" strokeWidth={1}/>
      {/* Fletching */}
      <polygon points={`-18,0 -10,-6 -12,0`} fill="#FF5722" opacity=".9"/>
      <polygon points={`-18,0 -10,6 -12,0`} fill="#FF9800" opacity=".9"/>
      <line x1={-14} y1={-6} x2={-14} y2={6} stroke="#4E342E" strokeWidth={.8}/>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TARGET ITEM (swinging on rope)
// ─────────────────────────────────────────────────────────────────────────────
function TargetSVG({ target, hit }) {
  const ax = target.anchorX;
  const ropeLen = target.ropeLen;
  const angle = target.angle; // radians
  const tx = ax + Math.sin(angle) * ropeLen;
  const ty = 28 + Math.cos(angle) * ropeLen;

  const t = target.item;

  return (
    <g>
      {/* Rope */}
      <line x1={ax} y1={28} x2={tx} y2={ty}
        stroke="#9B7A2A" strokeWidth={3} strokeLinecap="round" opacity={hit ? 0.3 : 1}/>
      {/* Rope texture marks */}
      {Array.from({length:5},(_,i)=>(
        <circle key={i}
          cx={ax + (tx-ax)*(i+1)/6}
          cy={28 + (ty-28)*(i+1)/6}
          r={1.5} fill="#C8A84B" opacity={hit ? 0 : .6}
        />
      ))}

      {/* Target container */}
      {!hit && (
        <g transform={`translate(${tx},${ty})`}>
          {/* Outer glow */}
          <circle r={TARGET_R+8} fill={`${t.glow}18`} style={{filter:"blur(4px)"}}/>
          {/* Ring */}
          <circle r={TARGET_R+4} fill="none" stroke={t.glow} strokeWidth={1.5} opacity=".5"
            strokeDasharray="5 3"/>
          {/* Container plate */}
          <circle r={TARGET_R} fill={`${t.color}22`} stroke={t.color} strokeWidth={2}/>
          <circle r={TARGET_R-4} fill={`${t.color}15`}/>
          {/* Emoji */}
          <text x={0} y={9} textAnchor="middle" fontSize={28}
            style={{filter:`drop-shadow(0 0 6px ${t.glow})`}}>
            {t.emoji}
          </text>
          {/* Points label */}
          <text x={0} y={TARGET_R+16} textAnchor="middle" fontSize={11}
            fill={t.glow} fontFamily="'Exo 2',sans-serif" fontWeight="800" letterSpacing="1">
            +{t.pts}
          </text>
          {/* Tie string */}
          <line x1={0} y1={-TARGET_R} x2={0} y2={-TARGET_R-8} stroke="#9B7A2A" strokeWidth={3}/>
        </g>
      )}

      {/* Hit burst */}
      {hit && (
        <g transform={`translate(${tx},${ty})`} style={{animation:"targetHit .5s ease-out forwards"}}>
          {[0,60,120,180,240,300].map((a,i)=>(
            <line key={i}
              x1={0} y1={0}
              x2={Math.cos(a*Math.PI/180)*35}
              y2={Math.sin(a*Math.PI/180)*35}
              stroke={t.glow} strokeWidth={2.5} strokeLinecap="round"
              opacity=".8"
            />
          ))}
          <circle r={18} fill={`${t.glow}40`}/>
          <text x={0} y={8} textAnchor="middle" fontSize={26}>{t.emoji}</text>
        </g>
      )}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ARCHER CHARACTER
// ─────────────────────────────────────────────────────────────────────────────
function ArcherSVG({ angle, pulling }) {
  const lean = pulling ? -8 : 0;
  return (
    <g transform={`translate(${BOW_X},${BOW_Y}) rotate(${lean})`} style={{transformOrigin:`${BOW_X}px ${BOW_Y}px`}}>
      {/* Legs */}
      <line x1={-10} y1={10} x2={-16} y2={42} stroke="#1565C0" strokeWidth={12} strokeLinecap="round"/>
      <line x1={10} y1={10} x2={16} y2={42} stroke="#1565C0" strokeWidth={12} strokeLinecap="round"/>
      {/* Feet */}
      <ellipse cx={-18} cy={44} rx={10} ry={5} fill="#4E342E"/>
      <ellipse cx={18} cy={44} rx={10} ry={5} fill="#4E342E"/>
      {/* Body */}
      <ellipse cx={0} cy={-5} rx={18} ry={24} fill="#E91E63" stroke="#AD1457" strokeWidth={1.5}/>
      {/* Body highlight */}
      <ellipse cx={-6} cy={-12} rx={7} ry={10} fill="rgba(255,255,255,.15)"/>
      {/* Neck */}
      <rect x={-6} y={-28} width={12} height={9} rx={4} fill="#FFCCBC"/>
      {/* Head */}
      <ellipse cx={0} cy={-38} rx={16} ry={15} fill="#FFCCBC" stroke="#E0A070" strokeWidth={1}/>
      {/* Hair */}
      <path d="M -15,-44 Q 0,-54 15,-44 Q 12,-32 -12,-32" fill="#212121"/>
      {/* Eyes */}
      <ellipse cx={-5} cy={-40} rx={3.5} ry={4} fill="#fff"/>
      <ellipse cx={5} cy={-40} rx={3.5} ry={4} fill="#fff"/>
      <circle cx={-4} cy={-39} r={2.2} fill="#1a0a00"/>
      <circle cx={6} cy={-39} r={2.2} fill="#1a0a00"/>
      <circle cx={-3} cy={-40} r={.9} fill="#fff"/>
      <circle cx={7} cy={-40} r={.9} fill="#fff"/>
      {/* Determined brow */}
      <path d="M -8,-44 Q -5,-46 -2,-44" stroke="#5D4037" strokeWidth={1.8} fill="none" strokeLinecap="round"/>
      <path d="M 2,-44 Q 5,-46 8,-44" stroke="#5D4037" strokeWidth={1.8} fill="none" strokeLinecap="round"/>
      {/* Smile */}
      <path d="M -5,-33 Q 0,-29 5,-33" stroke="#E0A070" strokeWidth={1.5} fill="none" strokeLinecap="round"/>
      {/* Arms (just body stubs — bow is separate SVG on top) */}
      <line x1={16} y1={-10} x2={28} y2={-18} stroke="#FFCCBC" strokeWidth={10} strokeLinecap="round"/>
      <line x1={-16} y1={-10} x2={-26} y2={-6} stroke="#FFCCBC" strokeWidth={10} strokeLinecap="round"/>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN GAME
// ─────────────────────────────────────────────────────────────────────────────
export default function BowAndArrow() {
  const [screen,      setScreen]      = useState("menu");
  const [selLevel,    setSelLevel]    = useState(0);
  const [score,       setScore]       = useState(0);
  const [arrows,      setArrows]      = useState(TOTAL_ARROWS);
  const [targets,     setTargets]     = useState([]);
  const [flyArrows,   setFlyArrows]   = useState([]);
  const [hitEffects,  setHitEffects]  = useState([]);
  const [floatScores, setFloatScores] = useState([]);
  const [pulling,     setPulling]     = useState(false);
  const [pullPos,     setPullPos]     = useState({x:0,y:0});
  const [bowAngle,    setBowAngle]    = useState(0);
  const [pullDist,    setPullDist]    = useState(0);
  const [aimDots,     setAimDots]     = useState([]);
  const [gameOver,    setGameOver]    = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [confetti,    setConfetti]    = useState(false);
  const [highScore,   setHighScore]   = useState(()=>+localStorage.getItem("bow_hs")||0);
  const [newRecord,   setNewRecord]   = useState(false);
  const [hits,        setHits]        = useState(0);
  const [combo,       setCombo]       = useState(0);
  const [maxCombo,    setMaxCombo]    = useState(0);

  const rafRef      = useRef(null);
  const canvasRef   = useRef(null);
  const scoreRef    = useRef(0);
  const arrowsRef   = useRef(TOTAL_ARROWS);
  const targetsRef  = useRef([]);
  const flyRef      = useRef([]);
  const hitsRef     = useRef(0);
  const comboRef    = useRef(0);
  const maxComboRef = useRef(0);
  const gameActiveRef = useRef(false);
  const timeRef     = useRef(0);
  const floatsRef   = useRef([]);
  const hitSetRef   = useRef(new Set());

  const level = LEVELS[selLevel];

  // ── Build targets for current level ────────────────────────────────────────
  const buildTargets = useCallback(() => {
    const types = [...TARGET_TYPES].sort(()=>Math.random()-.5).slice(0, level.targets);
    return types.map((t, i) => ({
      id: uid(),
      item: t,
      anchorX: level.ropes[i],
      ropeLen: rand(110, 160),
      angle: rand(-0.4, 0.4),
      angleV: (Math.random()>.5?1:-1) * level.swingSpeed * rand(0.8,1.2),
      swingAmp: level.swingAmp * Math.PI / 180,
      hit: false,
      hitTime: 0,
    }));
  }, [level]);

  // ── Game loop ───────────────────────────────────────────────────────────────
  const gameLoop = useCallback((timestamp) => {
    if (!gameActiveRef.current) return;
    const dt = timestamp - (timeRef.current || timestamp);
    timeRef.current = timestamp;

    // Update target swinging (pendulum physics)
    targetsRef.current = targetsRef.current.map(t => {
      if (t.hit) return t;
      // Pendulum: angular acceleration = -g/L * sin(angle) * ampFactor
      const acc = -(0.012) * Math.sin(t.angle) * (level.swingAmp / 30);
      const newV = (t.angleV + acc) * 0.999;
      const newA = t.angle + newV;
      // Clamp
      const clampedA = Math.max(-t.swingAmp, Math.min(t.swingAmp, newA));
      return { ...t, angle: clampedA, angleV: newV };
    });
    setTargets([...targetsRef.current]);

    // Update flying arrows
    const updatedArrows = flyRef.current.map(a => {
      const newTrail = [{x:a.x,y:a.y}, ...(a.trail||[]).slice(0,7)];
      return {
        ...a,
        x: a.x + a.vx,
        y: a.y + a.vy,
        vy: a.vy + GRAVITY,
        trail: newTrail,
      };
    });

    // Check collisions
    const newFloats = [];
    const newHitIds = new Set();
    let newHits = 0;
    let newComboInc = 0;

    const survivingArrows = updatedArrows.filter(arrow => {
      // Out of bounds
      if (arrow.x < -20 || arrow.x > W+20 || arrow.y > H+20) return false;

      // Check against each target
      for (let ti = 0; ti < targetsRef.current.length; ti++) {
        const t = targetsRef.current[ti];
        if (t.hit) continue;

        const ax2 = t.anchorX + Math.sin(t.angle) * t.ropeLen;
        const ay2 = 28 + Math.cos(t.angle) * t.ropeLen;
        const d = Math.sqrt((arrow.x-ax2)**2 + (arrow.y-ay2)**2);

        if (d < TARGET_R + 8) {
          // HIT!
          targetsRef.current[ti] = { ...t, hit:true, hitTime:Date.now() };
          newHitIds.add(ti);
          hitsRef.current++;
          comboRef.current++;
          if (comboRef.current > maxComboRef.current) maxComboRef.current = comboRef.current;
          const comboBonus = comboRef.current > 1 ? comboRef.current * 25 : 0;
          const pts = t.item.pts + comboBonus;
          scoreRef.current += pts;
          setScore(scoreRef.current);
          setHits(hitsRef.current);
          setCombo(comboRef.current);
          setMaxCombo(maxComboRef.current);
          newHits++;
          // Float score
          const fid = uid();
          floatsRef.current.push({ id:fid, x:ax2, y:ay2-40, pts, color:t.item.glow });
          setTimeout(()=>{ floatsRef.current = floatsRef.current.filter(f=>f.id!==fid); setFloatScores([...floatsRef.current]); }, 1000);
          return false; // remove arrow
        }
      }
      return true;
    });

    if (newHitIds.size > 0) setTargets([...targetsRef.current]);
    setFloatScores([...floatsRef.current]);
    flyRef.current = survivingArrows;
    setFlyArrows([...survivingArrows]);

    // Check if round over (all arrows fired and no arrows in flight)
    if (arrowsRef.current <= 0 && flyRef.current.length === 0) {
      endRound();
      return;
    }

    rafRef.current = requestAnimationFrame(gameLoop);
  }, [level]);

  const endRound = useCallback(() => {
    gameActiveRef.current = false;
    cancelAnimationFrame(rafRef.current);
    const final = scoreRef.current;
    const allHit = targetsRef.current.every(t=>t.hit);
    const isNew = final > highScore;
    if (isNew) { setHighScore(final); localStorage.setItem("bow_hs", final); setNewRecord(true); }
    setFinalResult({
      score: final,
      hits: hitsRef.current,
      total: targetsRef.current.length,
      maxCombo: maxComboRef.current,
      allHit,
    });
    if (allHit) setConfetti(true);
    setGameOver(true);
    setScreen("gameover");
  }, [highScore]);

  // ── Fire arrow ──────────────────────────────────────────────────────────────
  const fireArrow = useCallback(() => {
    if (!pulling || arrowsRef.current <= 0 || !gameActiveRef.current) return;

    const dx = pullPos.x - BOW_X;
    const dy = pullPos.y - BOW_Y;
    const d  = Math.sqrt(dx*dx+dy*dy);
    if (d < 8) return;

    const power = Math.min(d / MAX_PULL, 1);
    // Arrow fires OPPOSITE to pull direction
    const vx = (-dx/d) * ARROW_SPEED * power;
    const vy = (-dy/d) * ARROW_SPEED * power;

    const arrow = { id:uid(), x:BOW_X, y:BOW_Y, vx, vy, trail:[] };
    flyRef.current = [...flyRef.current, arrow];
    setFlyArrows([...flyRef.current]);

    arrowsRef.current -= 1;
    setArrows(arrowsRef.current);
    comboRef.current = 0; // reset combo on miss (will re-increment on hit)
    setPulling(false);
    setPullDist(0);
    setAimDots([]);
  }, [pulling, pullPos]);

  // ── Pointer events ──────────────────────────────────────────────────────────
  const getPos = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return {x:0,y:0};
    const scaleX = W / rect.width;
    const scaleY = H / rect.height;
    const cx = (e.clientX ?? e.touches?.[0]?.clientX ?? 0);
    const cy = (e.clientY ?? e.touches?.[0]?.clientY ?? 0);
    return { x:(cx-rect.left)*scaleX, y:(cy-rect.top)*scaleY };
  };

  const onPointerDown = useCallback((e) => {
    if (!gameActiveRef.current || arrowsRef.current<=0) return;
    e.preventDefault();
    const pos = getPos(e);
    setPulling(true);
    setPullPos(pos);
    setPullDist(0);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!pulling) return;
    e.preventDefault();
    const pos = getPos(e);
    setPullPos(pos);
    const dx = pos.x - BOW_X;
    const dy = pos.y - BOW_Y;
    const d  = Math.min(Math.sqrt(dx*dx+dy*dy), MAX_PULL);
    setPullDist(d);

    // Angle bow toward pull
    const angle = Math.atan2(-dy, -dx) * 180 / Math.PI - 90;
    setBowAngle(angle);

    // Compute aim trajectory dots
    const power = d / MAX_PULL;
    const adx = (-dx/Math.sqrt(dx*dx+dy*dy)) * ARROW_SPEED * power;
    const ady = (-dy/Math.sqrt(dx*dx+dy*dy)) * ARROW_SPEED * power;
    const dots = [];
    let ax = BOW_X, ay = BOW_Y, avx = adx, avy = ady;
    for (let i = 0; i < 14; i++) {
      ax += avx; ay += avy; avy += GRAVITY;
      if (i % 2 === 0) dots.push({x:ax, y:ay, o: 1 - i/14});
    }
    setAimDots(dots);
  }, [pulling]);

  const onPointerUp = useCallback((e) => {
    if (!pulling) return;
    e.preventDefault();
    fireArrow();
  }, [pulling, fireArrow]);

  // ── Start game ──────────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    scoreRef.current = 0; arrowsRef.current = TOTAL_ARROWS;
    hitsRef.current = 0; comboRef.current = 0; maxComboRef.current = 0;
    flyRef.current = []; floatsRef.current = []; hitSetRef.current = new Set();
    timeRef.current = 0;

    const newTargets = buildTargets();
    targetsRef.current = newTargets;

    setScore(0); setArrows(TOTAL_ARROWS); setTargets(newTargets);
    setFlyArrows([]); setFloatScores([]); setHitEffects([]);
    setPulling(false); setPullDist(0); setBowAngle(0); setAimDots([]);
    setGameOver(false); setFinalResult(null); setConfetti(false);
    setHits(0); setCombo(0); setMaxCombo(0); setNewRecord(false);

    setScreen("playing");
    gameActiveRef.current = true;
    rafRef.current = requestAnimationFrame(gameLoop);
  }, [buildTargets, gameLoop]);

  useEffect(() => () => { cancelAnimationFrame(rafRef.current); }, []);

  const allHit = targets.every(t=>t.hit);
  const noArrows = arrows <= 0 && flyArrows.length === 0;

  return (
    <>
      <style>{CSS}</style>
      <div className="root">

        {/* ── MENU ── */}
        {screen === "menu" && (
          <div className="menu">
            {/* Lanterns */}
            {[[5,4,2.6],[88,3,2.1],[15,9,1.7],[82,10,2.3]].map(([l,t,s],i)=>(
              <div key={i} className="lantern" style={{left:`${l}%`,top:`${t}%`,fontSize:`${s}rem`,animationDuration:`${2.5+i*.4}s`,animationDelay:`${i*.35}s`}}>🏮</div>
            ))}
            <div className="menu-title">Bow & Arrow<br/>Festival</div>
            <div className="menu-si">ආයුධ ක්‍රීඩාව 🏹</div>
            <div className="menu-preview">🏹</div>
            <div className="menu-desc">
              🍮 Swinging <strong>Kevum</strong> & <strong>Kokis</strong> hang from the beam<br/>
              🎯 Pull back the bow to aim your arrow<br/>
              🏹 You have <strong>3 arrows</strong> — use them wisely!<br/>
              🔥 Chain hits for <strong>combo bonus</strong> points!
            </div>
            <div className="level-grid">
              {LEVELS.map((l,i)=>(
                <div key={l.id} className={`level-card ${selLevel===i?"sel":""}`} onClick={()=>setSelLevel(i)}>
                  <div className="lc-num">{l.id}</div>
                  <div>
                    <div className="lc-name">{l.si}</div>
                    <div className="lc-en">{l.label} · {l.targets} targets</div>
                  </div>
                </div>
              ))}
            </div>
            <button className="play-btn" onClick={startGame}>🏹 SHOOT!</button>
            {highScore>0 && <div className="hs-row">BEST: {highScore} pts</div>}
          </div>
        )}

        {/* ── PLAYING ── */}
        {screen === "playing" && (
          <>
            {/* HUD */}
            <div className="hud">
              <div className="score-box">
                <div className="score-lbl">SCORE</div>
                <div className="score-val">{score}</div>
              </div>
              <div className="hud-mid">
                <div className="level-name">{level.label} · {level.si}</div>
                <div className="arrow-row">
                  {Array.from({length:TOTAL_ARROWS},(_,i)=>(
                    <span key={i} className={`arrow-icon ${i>=arrows?"used":""}`}>🏹</span>
                  ))}
                </div>
              </div>
              <div className="level-badge">
                <div className="level-lbl">COMBO</div>
                <div className="level-val">{combo > 1 ? `🔥×${combo}` : combo}</div>
              </div>
            </div>

            {/* Canvas */}
            <div
              className="canvas"
              ref={canvasRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              style={{cursor: arrows>0 && gameActiveRef.current ? "crosshair":"default"}}
            >
              {/* Background */}
              <SceneSVG/>

              {/* Game SVG layer */}
              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={{zIndex:10}}>
                <defs>
                  <linearGradient id="arrowTip" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"  stopColor="#E0E0E0"/>
                    <stop offset="100%" stopColor="#BDBDBD"/>
                  </linearGradient>
                </defs>

                {/* Targets */}
                {targets.map(t => (
                  <TargetSVG key={t.id} target={t} hit={t.hit}/>
                ))}

                {/* Aim trajectory dots */}
                {pulling && aimDots.map((d,i)=>(
                  <circle key={i} cx={d.x} cy={d.y} r={3-i*.12}
                    fill="#FFD700" opacity={d.o*.7}
                    style={{filter:"blur(.5px)"}}
                  />
                ))}

                {/* Flying arrows */}
                {flyArrows.map(a => <ArrowSVG key={a.id} arrow={a}/>)}

                {/* Archer */}
                <ArcherSVG angle={bowAngle} pulling={pulling}/>

                {/* Bow */}
                <BowSVG angle={bowAngle} pullDist={pullDist} pulling={pulling}/>

                {/* Pull indicator ring */}
                {pulling && pullDist > 5 && (
                  <circle
                    cx={BOW_X} cy={BOW_Y}
                    r={pullDist * 0.8 + 10}
                    fill="none"
                    stroke="rgba(255,215,0,0.25)"
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                  />
                )}
              </svg>

              {/* Floating scores */}
              {floatScores.map(f=>(
                <div key={f.id} className="float-score" style={{
                  left: f.x * (canvasRef.current?.getBoundingClientRect().width||W) / W,
                  top:  f.y * (canvasRef.current?.getBoundingClientRect().height||H) / H,
                  color:f.color,
                }}>+{f.pts}</div>
              ))}

              {/* All hit celebration */}
              {allHit && (
                <div className="overlay">
                  <Confetti/>
                  <div className="result-stamp" style={{color:"#FFD700"}}>🎯 PERFECT!</div>
                  <div className="result-si">සියලුම ඉලක්ක !</div>
                  <div className="result-pts" style={{color:"#69F0AE"}}>+{score} pts</div>
                </div>
              )}

              {/* Out of arrows */}
              {!allHit && noArrows && (
                <div className="overlay">
                  <div className="result-stamp" style={{color:"#FF9800"}}>🏹 ARROWS SPENT!</div>
                  <div className="result-si">ඊතල ඉවරයි!</div>
                  <div className="result-pts" style={{color:"var(--gold)"}}>Score: {score}</div>
                </div>
              )}

              {/* Pull hint */}
              {!pulling && arrows > 0 && !allHit && !noArrows && (
                <div style={{
                  position:"absolute", bottom:"8%", left:"50%",
                  transform:"translateX(-50%)",
                  background:"rgba(0,0,0,.55)", borderRadius:20,
                  padding:".3rem .9rem",
                  fontSize:".65rem", color:"rgba(255,215,0,.6)",
                  letterSpacing:1, fontFamily:"'Exo 2',sans-serif",
                  pointerEvents:"none", zIndex:20,
                  border:"1px solid rgba(255,215,0,.2)",
                }}>
                  👆 Press & drag to aim · Release to fire
                </div>
              )}
            </div>
          </>
        )}

        {/* ── GAME OVER ── */}
        {screen === "gameover" && finalResult && (
          <div className="gameover">
            {confetti && <Confetti/>}
            <div style={{fontSize:"2.8rem"}}>🏹</div>
            <div className="go-title">
              {finalResult.allHit
                ? "Perfect Archer! 🏆"
                : finalResult.hits === finalResult.total
                ? "Excellent! 🎯"
                : finalResult.hits > 0
                ? "Good Shot! 🎉"
                : "Keep Practicing! 💪"}
            </div>
            <div style={{fontFamily:"'Noto Sans Sinhala',sans-serif",color:"rgba(255,200,100,.55)",fontSize:".8rem"}}>
              සුභ අලුත් අවුරුද්දක් වේවා! 🎊
            </div>
            <div className="go-card">
              <div style={{fontSize:".48rem",color:"rgba(255,255,255,.28)",letterSpacing:3,fontFamily:"'Exo 2',sans-serif"}}>FINAL SCORE</div>
              <div className="go-score">{finalResult.score}</div>
              {newRecord && <span className="new-rec">🏆 NEW RECORD!</span>}
              <div className="go-grid">
                <div className="go-stat"><div className="go-sl">TARGETS HIT</div><div className="go-sv">{finalResult.hits}/{finalResult.total}</div></div>
                <div className="go-stat"><div className="go-sl">MAX COMBO</div><div className="go-sv">🔥{finalResult.maxCombo}</div></div>
                <div className="go-stat"><div className="go-sl">ARROWS USED</div><div className="go-sv">{TOTAL_ARROWS} / {TOTAL_ARROWS}</div></div>
                <div className="go-stat"><div className="go-sl">BEST SCORE</div><div className="go-sv">{highScore}</div></div>
              </div>
            </div>
            <div className="go-btns">
              <button className="go-a" onClick={startGame}>PLAY AGAIN</button>
              <button className="go-b" onClick={()=>setScreen("menu")}>MENU</button>
            </div>
            <div style={{fontSize:"1.2rem",letterSpacing:5}}>🏹🎯🍮🌸🏆</div>
          </div>
        )}
      </div>
    </>
  );
}